/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B060010 製造チェック照会
 */
import { Column, Query } from "@material-table/core";
import {
  B060010Find,
  B060010FindRequest,
  B060010GetExcel,
  B060010ExcelRequest,
  catchRestError,
  catchFileError,
} from "clients/rest";
import { B000140OperatorNm } from "clients/rest/b000/B000140";
import { Box } from "@mui/material";
import {
  SText,
  SCodeSelect,
  SDateField,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
  SFileButton,
  msgState,
} from "components";
import {
  B060010Shutsuryokujun,
  getSCode,
  KanryoKbn,
  ListhakkozumiKbn,
  SCode,
  SeisanKbn,
  ShohinshubetsuKbn,
} from "messages/codes";
import { labels } from "messages/labels";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { date, dateWithSlashAt, download, mapIds } from "services";
import { authState, focusState, useFocus } from "states";
import { B000140 } from "pages";
import { getClientError, getClientMessage } from "messages";
import { useNavigate } from "react-router-dom";
import { kanryoKbn } from "services/codes";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  listhakkozumiKbn: SCode;
  seisanKbn: SCode;
  operatorCd: string;
  operatorNm: string;
  juchuYmdFrom: string;
  juchuYmdTo: string;
  seisakuYmdFrom: string;
  seisakuYmdTo: string;
  juchudenpyoNoFrom: string;
  juchudenpyoNoTo: string;
  kanryoKbn: SCode;
  shutsuryokujun1: SCode;
  shutsuryokujun2: SCode;
  shutsuryokujun3: SCode;
}>;
// テーブルデータ定義
type Data = Partial<{
  gyoNo: number;
  eigyoCd: string;
  eigyoNmr: string;
  juchudenpyoNo: number;
  juchudenpyoNoF: string;
  juchuYmd: string;
  tokuiCd: string;
  tokuishitenCd: string;
  tokuiNm: string;
  nonyuCd: string;
  nonyushitenCd: string;
  nonyuNm: string;
  shainCd: string;
  shainNm: string;
  biko: string;
  shohinshubetsuKbn: string;
  juchugyoNo: number;
  seizoNo: number;
  meisaiShohinCd: string;
  meisaiShohinNm: string;
  meisaiVol: string;
  meisaibiko: string;
  meishoNm1: string;
  maisaiWhsoto: string;
  meisaiWhuchi: string;
  seisakuYmd: string;
  juchusu: number;
  shireYmd: string;
  shiresu: number;
  meisaiJodaitanka: number;
  seizozansu: number;
  kanryoKbn: number;
  juchukoseigyoNo: number;
  koseiShohinCd: string;
  koseiShohinNm: string;
  koseiVol: string;
  meisaikoseiBiko: string;
  koseiWhsoto: string;
  koseiWhuchi: string;
  koseisu: number;
  koseiJodaitanka: number;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  listhakkozumiKbn: getSCode(ListhakkozumiKbn, "0"),
  seisanKbn: undefined,
  operatorCd: "",
  operatorNm: "",
  juchuYmdFrom: date(),
  juchuYmdTo: date(),
  seisakuYmdFrom: date(),
  seisakuYmdTo: date(),
  juchudenpyoNoFrom: "",
  juchudenpyoNoTo: "",
  kanryoKbn: getSCode(KanryoKbn, "0"),
  shutsuryokujun1: getSCode(B060010Shutsuryokujun, "0"),
  shutsuryokujun2: getSCode(B060010Shutsuryokujun, "1"),
  shutsuryokujun3: getSCode(B060010Shutsuryokujun, "2"),
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", hidden: true },
  { field: "eigyoCd", hidden: true },
  { field: "eigyoNmr", title: "営業所", width: "15rem" },
  { field: "juchudenpyoNoF", title: "受注No.", width: "7rem" },
  { field: "juchudenpyoNo", hidden: true },

  {
    field: "juchuYmd",
    title: "受注日",
    width: "6rem",
    render: (data) => {
      if (data.juchuYmd === null) {
        return "";
      }
      if (data.juchuYmd !== "") {
        return dateWithSlashAt(data.juchuYmd!);
      }
    },
  },
  { field: "tokuiCd", title: "得意先コード", width: "5rem" },
  { field: "tokuishitenCd", title: "得意先支店コード", width: "7rem" },
  { field: "tokuiNm", title: "得意先名", width: "20rem" },
  { field: "nonyuCd", title: "納入先コード", width: "5rem" },
  { field: "nonyushitenCd", title: "納入先支店コード", width: "7rem" },
  { field: "nonyuNm", title: "納入先名", width: "20rem" },
  { field: "shainCd", hidden: true },
  { field: "shainNm", title: "指示担当者", width: "20rem" },
  { field: "biko", title: "製造備考", width: "20rem" },
  { field: "shohinshubetsuKbn", hidden: true },
  {
    field: "seisanKbnView",
    title: "生産区分",
    width: "8rem",
    render: (data) => {
      return getSCode(
        ShohinshubetsuKbn,
        data.shohinshubetsuKbn?.toString() ?? "0"
      )?.label;
    },
  },
  { field: "juchugyoNo", title: "行　", width: "4rem", align: "right" },
  { field: "seizoNo", title: "製造番号　", width: "5rem", align: "right" },
  { field: "meisaiShohinCd", title: "商品コード", width: "10rem" },
  { field: "meisaiShohinNm", title: "商品名", width: "20rem" },
  { field: "meisaiVol", title: "VOL", width: "10rem" },
  { field: "meisaibiko", title: "備考", width: "20rem" },
  { field: "meishoNm1", title: "加工種別", width: "20rem" },
  { field: "meisaiWhsoto", title: "WH外　", width: "10rem", align: "right" },
  { field: "meisaiWhuchi", title: "WH内　", width: "10rem", align: "right" },
  {
    field: "seisakuYmd",
    title: "製作日",
    width: "10rem",
    render: (data) => {
      if (data.seisakuYmd === null) {
        return "";
      }
      if (data.seisakuYmd !== "") {
        return dateWithSlashAt(data.seisakuYmd!);
      }
    },
  },
  {
    field: "juchusu",
    title: "完成予定数　",
    width: "6rem",
    align: "right",
    render: (data) => data.juchusu?.toLocaleString(),
  },
  {
    field: "shireYmd",
    title: "完成日",
    width: "10rem",
    render: (data) => {
      if (data.shireYmd === null) {
        return "";
      }
      if (data.shireYmd !== "") {
        return dateWithSlashAt(data.shireYmd!);
      }
    },
  },
  {
    field: "shiresu",
    title: "完成数　",
    width: "6rem",
    align: "right",
    render: (data) => data.shiresu?.toLocaleString(),
  },
  {
    field: "meisaiJodaitanka",
    title: "上代　",
    width: "9rem",
    align: "right",
    render: (data) => data.meisaiJodaitanka?.toLocaleString(),
  },
  {
    field: "seizozansu",
    title: "製造残数　",
    width: "6rem",
    align: "right",
    render: (data) => data.seizozansu?.toLocaleString(),
  },
  {
    field: "kanryoKbn",
    title: "完了区分",
    width: "6rem",
    render: (data) => {
      if (data.kanryoKbn === kanryoKbn.incomplete) {
        return "";
      } else if (data.kanryoKbn === kanryoKbn.completed) {
        return "＊";
      }
    },
  },
  {
    field: "juchukoseigyoNo",
    title: "行　",
    width: "3rem",
    align: "right",
    render: (data) => {
      if (!data.juchukoseigyoNo) {
        return "";
      } else {
        return data.juchukoseigyoNo;
      }
    },
  },
  { field: "koseiShohinCd", title: "商品コード", width: "10rem" },
  { field: "koseiShohinNm", title: "商品名", width: "20rem" },
  { field: "koseiVol", title: "VOL", width: "10rem" },
  { field: "meisaikoseiBiko", title: "備考", width: "15rem" },
  { field: "koseiWhsoto", title: "WH外　", width: "10rem", align: "right" },
  { field: "koseiWhuchi", title: "WH内　", width: "10rem", align: "right" },
  {
    field: "koseisu",
    title: "構成数　",
    width: "6rem",
    align: "right",
    render: (data) => data.koseisu?.toLocaleString(),
  },
  {
    field: "koseiJodaitanka",
    title: "上代　",
    width: "9rem",
    align: "right",
    render: (data) => data.koseiJodaitanka?.toLocaleString(),
  },
];

const focusId = "B060010";

// B060010 製造チェック照会
export const B060010 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  const [totalCount, setTotalCount] = useState(0);
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするステート
  const isDefaultPage = useRef(false);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();
  // ボタン活性制御
  const [disabledButton, setDisabledButton] = useState(true);
  // ログインユーザー情報
  const auth = useRecoilValue(authState);

  // 初期表示イベント
  useEffect(() => {
    (async () => {
      // 初期表示
      await methods.setValue("operatorCd", auth?.shainCd);
      await methods.setValue("operatorNm", auth?.shainNm);
      // 初回フォーカス先
      focus(1);
    })();
  }, []);

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 出力順の設定
      // 出力順1
      let order1;
      if (fields.shutsuryokujun1?.value === "0") {
        order1 = "juchuYmd,asc";
      } else if (fields.shutsuryokujun1?.value === "1") {
        order1 = "seisakuYmd,asc";
      } else if (fields.shutsuryokujun1?.value === "2") {
        order1 = "tokuiNm,asc";
      } else {
        order1 = "";
      }
      // 出力順2
      let order2;
      if (fields.shutsuryokujun2?.value === "0") {
        order2 = "juchuYmd,asc";
      } else if (fields.shutsuryokujun2?.value === "1") {
        order2 = "seisakuYmd,asc";
      } else if (fields.shutsuryokujun2?.value === "2") {
        order2 = "tokuiNm,asc";
      } else {
        order2 = "";
      }
      // 出力順3
      let order3;
      if (fields.shutsuryokujun3?.value === "0") {
        order3 = "juchuYmd,asc";
      } else if (fields.shutsuryokujun3?.value === "1") {
        order3 = "seisakuYmd,asc";
      } else if (fields.shutsuryokujun3?.value === "2") {
        order3 = "tokuiNm,asc";
      } else {
        order3 = "";
      }
      // パラメータに設定する出力順作成
      let order: string[] = [
        order1,
        order2,
        order3,
        "juchudenpyoNo,asc",
        "juchugyoNo,asc",
        "juchukoseigyoNo,asc",
      ];

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B060010FindRequest = {
        hakkoKbn: fields.listhakkozumiKbn?.value!,
        shohinshubetsuKbn: fields.seisanKbn?.value,
        shainCd: fields.operatorCd,
        juchuYmdFrom: fields.juchuYmdFrom,
        juchuYmdTo: fields.juchuYmdTo,
        seisakuYmdFrom: fields.seisakuYmdFrom,
        seisakuYmdTo: fields.seisakuYmdTo,
        juchudenpyoNoFrom: fields.juchudenpyoNoFrom,
        juchudenpyoNoTo: fields.juchudenpyoNoTo,
        kanryoKbn: fields.kanryoKbn?.value!,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: order,
      };
      // API呼び出し
      B060010Find(param)
        // 正常取得時
        .then((value) => {
          // 結果件数を参照しExcelボタン活性制御
          if (value.data.totalElements === 0) {
            // データ非存在エラーメッセージ表示
            setMsg(getClientError("B060010_001"));
            // Excelボタン非活性
            setDisabledButton(true);
          } else {
            // Excelボタン活性
            setDisabledButton(false);
          }
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
          setTotalCount(value.data.totalElements);
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          // Excelボタン非活性化
          setDisabledButton(true);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // Excelボタンクリックイベント
  const handleClickExcelButton = () => {
    // データ存在チェック
    if (totalCount === 0) {
      setMsg(getClientError("B060010_002"));
      return;
    }
    // Excel出力イベント
    handleExcel();
  };

  // EXCEL出力イベント
  const handleExcel = () => {
    // Excel出力フォームのデータを取得する。
    const fields = methods.getValues();
    // 出力順の設定
    // 出力順1
    let order1;
    if (fields.shutsuryokujun1?.value === "0") {
      order1 = "juchuYmd,asc";
    } else if (fields.shutsuryokujun1?.value === "1") {
      order1 = "seisakuYmd,asc";
    } else if (fields.shutsuryokujun1?.value === "2") {
      order1 = "tokuiNm,asc";
    } else {
      order1 = "";
    }
    // 出力順2
    let order2;
    if (fields.shutsuryokujun2?.value === "0") {
      order2 = "juchuYmd,asc";
    } else if (fields.shutsuryokujun2?.value === "1") {
      order2 = "seisakuYmd,asc";
    } else if (fields.shutsuryokujun2?.value === "2") {
      order2 = "tokuiNm,asc";
    } else {
      order2 = "";
    }
    // 出力順3
    let order3;
    if (fields.shutsuryokujun3?.value === "0") {
      order3 = "juchuYmd,asc";
    } else if (fields.shutsuryokujun3?.value === "1") {
      order3 = "seisakuYmd,asc";
    } else if (fields.shutsuryokujun3?.value === "2") {
      order3 = "tokuiNm,asc";
    } else {
      order3 = "";
    }
    // パラメータに設定する出力順作成
    let order: string[] = [
      order1,
      order2,
      order3,
      "juchudenpyoNo,asc",
      "juchugyoNo,asc",
      "juchukoseigyoNo,asc",
    ];
    // Excel出力パラメータを作成する・
    const param: B060010ExcelRequest = {
      hakkoKbn: fields.listhakkozumiKbn?.value!,
      kanryoKbn: fields.kanryoKbn?.value!,
      shohinshubetsuKbn: fields.seisanKbn?.value,
      shainCd: fields.operatorCd,
      juchuYmdFrom: fields.juchuYmdFrom,
      juchuYmdTo: fields.juchuYmdTo,
      seisakuYmdFrom: fields.seisakuYmdFrom,
      seisakuYmdTo: fields.seisakuYmdTo,
      juchudenpyoNoFrom: fields.juchudenpyoNoFrom,
      juchudenpyoNoTo: fields.juchudenpyoNoTo,
      sort: order,
    };
    B060010GetExcel(param)
      .then((response) => {
        download(response);
        setMsg(getClientMessage("B060010_001"));
      })
      .catch((reason) => {
        catchFileError(reason, navigate, setMsg);
      });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="製造チェック照会"
        gridTemplateColumns="8rem 9rem 1rem 8rem 1rem 8rem 1rem 2rem"
      >
        <SLabel>発行区分</SLabel>
        <SCodeSelect
          lable="発行区分"
          name="listhakkozumiKbn"
          options={ListhakkozumiKbn}
          gridColumn="2"
          {...register(1)}
        />

        <Box gridColumn="span 6" />

        <SLabel gridColumn="1">生産区分</SLabel>
        <SCodeSelect
          label="生産区分"
          name="seisanKbn"
          options={SeisanKbn}
          empty
          gridColumn="2"
          {...register(2)}
        />

        <Box gridColumn="span 6" />

        <SLabel gridColumn="1">オペレーターCD</SLabel>
        <SNumberField
          label="オペレーターコード"
          name="operatorCd"
          code
          size={7}
          gridColumn="2"
          onBlur={() => B000140OperatorNm(methods, navigate, setMsg)}
          dialog={<B000140 eigyoCd={""} bumonCd={""} />}
          handleSubmit={(data: any) => {
            methods.setValue("operatorCd", data.shainCd);
            methods.setValue("operatorNm", data.shainNm);
          }}
          {...register(3)}
        />
        <STextField
          label="オペレータ名"
          name="operatorNm"
          disabled
          gridColumn="span 4"
        />

        <Box gridColumn="span 2" />

        <SLabel gridColumn="1">受注日</SLabel>
        <SDateField name="juchuYmdFrom" gridColumn="2" {...register(4)} />
        <SText>～</SText>
        <SDateField name="juchuYmdTo" gridColumn="span 2" {...register(5)} />

        <Box gridColumn="span 3" />

        <SLabel gridColumn="1">製作日</SLabel>
        <SDateField name="seisakuYmdFrom" gridColumn="2" {...register(6)} />
        <SText>～</SText>
        <SDateField name="seisakuYmdTo" gridColumn="span 2" {...register(7)} />

        <Box gridColumn="span 3" />

        <SLabel gridColumn="1">受注No.</SLabel>
        <SNumberField
          name="juchudenpyoNoFrom"
          code
          size={10}
          gridColumn="2"
          {...register(8)}
        />
        <SText>～</SText>
        <SNumberField
          name="juchudenpyoNoTo"
          code
          size={10}
          gridColumn="span 2"
          {...register(9)}
        />

        <Box gridColumn="span 3" />

        <SLabel gridColumn="1">完了</SLabel>
        <SCodeSelect
          options={KanryoKbn}
          name="kanryoKbn"
          {...register(10)}
          gridColumn="2"
        />

        <Box gridColumn="span 6" />

        <SLabel>出力順</SLabel>
        <SCodeSelect
          options={B060010Shutsuryokujun}
          empty
          name="shutsuryokujun1"
          {...register(11)}
        />
        <SCodeSelect
          options={B060010Shutsuryokujun}
          name="shutsuryokujun2"
          empty
          gridColumn="span 2"
          {...register(12)}
        />
        <SCodeSelect
          options={B060010Shutsuryokujun}
          name="shutsuryokujun3"
          empty
          gridColumn="span 2"
          {...register(13)}
        />

        <SNormalButton
          label={labels.buttons.search}
          gridRow="9"
          gridColumn="6"
          align="right"
          {...register(14)}
          onClick={handleClickSearchButton}
        />
      </SFrame>
      <SFrame>
        <STable
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          totalCount={totalCount}
          width={1250}
          height={325}
        />
      </SFrame>
      <SFrame>
        <SFileButton
          label={labels.buttons.excel}
          align="right"
          onClick={handleClickExcelButton}
          disabled={disabledButton}
          {...register(14)}
        />
      </SFrame>
    </FormProvider>
  );
};
